<template>
  <header
    ref="siteHeader"
    class="site-header"
    :class="[
      {
        'inspector-mode': inspectorMode,
        'site-header--overlay': overlayActive,
        'site-header--search-overlay': searchActive
      },
      $class(headerState),
    ]"
    data-sign-post-priority="1"
    :js-sign-post="$string('accessibility.sign_posts.site_header')"
  >
    <div
      class="site-header__top"
      js-site-header="siteHeaderTop"
    >
      <div
        v-if="showMessageBar()"
        ref="siteMessaging"
        class="site-header__messaging"
      >
        <div
          v-if="liquid.settings.message_bar_mobile_message"
          class="site-header__messaging-mobile"
        >
          <div
            v-if="liquid.settings.message_bar_mobile_message"
            class="
              site-header__messaging-content
              site-header__messaging-content-mobile
              container
            "
          >
            <div
              class="text-body-xs"
              v-html="liquid.settings.message_bar_mobile_message"
            />
          </div>
        </div>

        <div
          v-if="
            liquid.settings.message_bar_message ||
              liquid.settings.message_bar_stores_url &&
              liquid.settings.message_bar_stores_message
          "
          class="site-header__messaging-desktop"
        >
          <div
            v-if="
              liquid.settings.message_bar_message ||
                liquid.settings.message_bar_stores_url &&
                liquid.settings.message_bar_stores_message
            "
            class="
              site-header__messaging-content
              site-header__messaging-content-desktop
              container
            "
          >
            <div
              v-if="liquid.settings.message_bar_message"
              @click="siteHeaderLinkDataLayer('create an account')"
              v-html="liquid.settings.message_bar_message"
            />

            <a
              v-if="
                liquid.settings.message_bar_stores_url &&
                  liquid.settings.message_bar_stores_message
              "
              :href="liquid.settings.message_bar_stores_url"
              @click="
                siteHeaderLinkDataLayer(
                  liquid.settings.message_bar_stores_message
                )
              "
              v-text="liquid.settings.message_bar_stores_message"
            />
          </div>
        </div>
      </div>

      <div
        v-if="showPromoBanner() && isMobile"
        ref="sitePromo"
        class="site-header__promo"
      >
        <swiper
          :effect="'fade'"
          :instance-name="liquid.sectionId"
          :modules="modules"
          :autoplay="{
            delay: 2500,
            disableOnInteraction: false
          }"
          class="site-header__promo-swiper container"
        >
          <swiper-slide
            v-if="liquid.settings.promo_banner_text_1"
          >
            <responsive-image
              v-if="promoImages.images.image1"
              :image="promoImages.images.image1"
              min_max="48-96"
              ratio="1:1"
            />

            <div v-html="liquid.settings.promo_banner_text_1" />
          </swiper-slide>

          <swiper-slide
            v-if="liquid.settings.promo_banner_text_2"
          >
            <responsive-image
              v-if="promoImages.images.image2"
              :image="promoImages.images.image2"
              min_max="48-96"
              ratio="1:1"
            />

            <div v-html="liquid.settings.promo_banner_text_2" />
          </swiper-slide>

          <swiper-slide
            v-if="liquid.settings.promo_banner_text_3"
          >
            <responsive-image
              v-if="promoImages.images.image3"
              :image="promoImages.images.image3"
              min_max="48-96"
              ratio="1:1"
            />

            <div v-html="liquid.settings.promo_banner_text_3" />
          </swiper-slide>
        </swiper>
      </div>

      <div
        ref="masthead"
        class="site-header__masthead"
      >
        <div class="site-header__main container">
          <a
            class="site-header__logo show-en"
            :href="$variable('routes.root')"
            :hidden="searchActive && isMobile"
            @click="siteHeaderLogoDataLayer()"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.aria_labels.homepage_url')"
            />
            <icon-watsons />
          </a>

          <a
            class="site-header__logo show-ar"
            :href="$variable('routes.root')"
            :hidden="searchActive && isMobile"
            @click="siteHeaderLogoLogoDataLayer()"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.aria_labels.homepage_url')"
            />
            <icon-watsonsarabic />
          </a>

          <div
            key="vue-header"
            class="site-header__main-right vue-header-right"
          >
            <search-bar
              :popular-search-link-list="popularSearchLinkList"
              @search-focus="searchActive = true"
              @search-blur="searchActive = false"
            />

            <a
              class="site-header__account icon-button"
              :href="$variable('routes.account.url')"
              @click="siteHeaderLinkDataLayer('account')"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.account')"
              />
              <icon-account />
            </a>

            <span
              @click="siteHeaderLinkDataLayer('wishlist')"
              v-html="`<!-- include 'wishlist-link' -->`"
            />

            <a
              v-if="$variable('routes.wishlist')"
              class="site-header__wishlist icon-button wishlist-static"
              :href="$variable('routes.wishlist')"
              @click="siteHeaderLinkDataLayer('wishlist')"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.wishlist')"
              />
              <icon-wishlist />
            </a>

            <a
              class="site-header__cart icon-button"
              :href="$variable('routes.cart.url')"
              @click="
                siteHeaderLinkDataLayer('cart'),
                handleCartToggle;
              "
              @mousedown="loadComponent('cart-drawer')"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.cart')"
              />

              <icon-bag />

              <span
                v-if="cart.item_count"
                class="site-header__cart-badge"
                :class="{ 'badge--large': cart.item_count > 9 }"
                aria-hidden="true"
                v-text="cart.item_count"
              />
            </a>
          </div>
        </div>
      </div>

      <nav class="site-header__navigation">
        <ul class="site-header__navigation-list container">
          <li
            v-for="(tier1, index) in desktop.items"
            :id="getParentKey(tier1, index)"
            :key="getParentKey(tier1, index)"
            class="site-header__navigation-item"
            :class="getNavigationItemClasses(tier1, index)"
            v-on="getParentEvents(tier1, index)"
          >
            <a
              class="site-header__navigation-link text-body-sbold"
              :href="tier1.url"
              @click="megaNavLinkDataLayer(tier1.title, 1)"
            >
              <span
                class="site-header__navigation-label"
                v-text="tier1.title"
              />
            </a>

            <button
              v-if="itemHasChildren(tier1)"
              class="site-header__navigation-arrow visually-hidden"
              type="button"
              @click="
                toggleParentHover(
                  getParentKey(tier1, index)
                )
              "
            >
              {{
                $string(
                  'general.site_header.show_hide_tier',
                  {
                    replace: {
                      action: parentHover === getParentKey(tier1, index) ? 'Hide' : 'Show',
                      title: tier1.title
                    }
                  }
                )
              }}

              <icon-chevron-down />
            </button>

            <div
              v-if="itemHasChildren(tier1)"
              class="site-header__mega-nav"
            >
              <transition
                name="fade"
                @enter="setMegaNavOverflowState"
              >
                <mega-nav
                  v-show="parentHover === getParentKey(tier1, index)"
                  :tier-title="tier1.title"
                  :items="tier1.items"
                  :content="tier1.content"
                  :wide="
                    getColumnCount(tier1.items) === 4 ||
                      megaNavOverflow.wide.includes(
                        getParentKey(tier1, index)
                      )
                  "
                />
              </transition>
            </div>
          </li>
        </ul>
      </nav>

      <teleport to="body">
        <transition name="fade">
          <div
            v-if="overlayActive || searchActive"
            class="site-header__overlay window-overlay"
            :class="{ 'site-header__overlay--search': searchActive }"
          />
        </transition>
      </teleport>
    </div>

    <div
      v-if="showPromoBanner()"
      :class="[
        $class(headerState),
      ]"
      class="site-header__promo-desktop"
      js-site-header="siteHeaderPromoDesktop"
    >
      <div class="container">
        <div
          v-if="liquid.settings.promo_banner_text_1"
          class="site-header__promo-message"
        >
          <responsive-image
            v-if="promoImages.images.image1"
            :image="promoImages.images.image1"
            min_max="48-96"
            ratio="1:1"
          />

          <div v-html="liquid.settings.promo_banner_text_1" />
        </div>

        <div
          v-if="liquid.settings.promo_banner_text_2"
          class="site-header__promo-message"
        >
          <responsive-image
            v-if="promoImages.images.image2"
            :image="promoImages.images.image2"
            min_max="48-96"
            ratio="1:1"
          />

          <div v-html="liquid.settings.promo_banner_text_2" />
        </div>

        <div
          v-if="liquid.settings.promo_banner_text_3"
          class="site-header__promo-message"
        >
          <responsive-image
            v-if="promoImages.images.image3"
            :image="promoImages.images.image3"
            min_max="48-96"
            ratio="1:1"
          />

          <div v-html="liquid.settings.promo_banner_text_3" />
        </div>
      </div>
    </div>

    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :template="false"
      type="drawer"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :is-active="isActive"
          :mobile="mobile.items"
        />
      </template>
    </overlay>
  </header>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import { debounce } from '~/helpers/general'

import timings from '~/config/timings'

import { A11y, EffectFade, Autoplay } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'

import MegaNav from '~global/mega-nav/mega-nav'
import SearchBar from '~global/search-bar/search-bar'
import ResponsiveImage from '~global/images/responsive-image'

import IconWatsons from '~icons/misc/watsons.svg'
import IconWatsonsarabic from '~icons/misc/watsonsArabic.svg'
import IconAccount from '~icons/misc/account.svg'
import IconWishlist from '~icons/misc/wishlist.svg'
import IconBag from '~icons/misc/bag.svg'
import IconChevronDown from '~icons/direction/chevron/down.svg'

/**
 * Returns the total number of navigation items, including children.
 * @param {Array} items - The top level menu items list.
 * @returns {Number}
 */
export function getTotalItems(items) {
  return items.reduce((accumulator, current) => {
    if (current.items && current.items.length) {
      accumulator += current.items.length
    }

    return accumulator
  }, items.length)
}

/**
 * Calculates the number of visual columns that a list of items should have.
 * @param {Array} items - The list of items.
 * @returns {Number}
 */
export function getColumnCount(items) {
  let count = 1
  const perColumn = 13
  const maxColumns = 4
  const totalItems = getTotalItems(items)

  /**
   * Iterates for each possible column to account for items per column.
   */
  for (let iterator = 1; iterator < maxColumns; iterator++) {
    if (totalItems > perColumn * iterator) {
      const columns = iterator + 1

      if (columns > maxColumns) {
        count = maxColumns
        return
      }

      count = columns
    }
  }

  return count
}

export default {
  name: 'SiteHeader',

  components: {
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/menu-drawer/menu-drawer'
      ),
    }),

    IconWatsons,
    IconWatsonsarabic,
    IconAccount,
    IconWishlist,
    IconBag,
    IconChevronDown,
    MegaNav,
    SearchBar,

    ResponsiveImage,
    Swiper,
    SwiperSlide,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    promoImages: {
      type: Object,
      default: () => ({
        images: [],
      }),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
    },
    popularSearchLinkList: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },
  },

  setup() {
    return {
      modules: [
        A11y,
        EffectFade,
        Autoplay,
      ],
    }
  },

  data() {
    return {
      height: {
        total: 0,
        visible: 0,
        searchOverlayTop: 0,
      },
      inspectorMode: false,
      parentHover: false,
      overlayActive: false,
      megaNavOverflow: {
        wide: [],
        right: [],
      },
      searchActive: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
      settings: (state) => state.index.settings,
    }),
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total': function() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible': function() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch header promo height to update CSS variable.
     */
    'height.promo': function() {
      document.documentElement.style.setProperty(
        '--header-promo-height',
        `${this.height.promo}px`,
      )
    },

    /**
     * Watch header promo height to update CSS variable.
     */
    'height.searchOverlayTop': function() {
      document.documentElement.style.setProperty(
        '--header-search-overlay-top',
        `${this.height.searchOverlayTop}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },

    /**
     * When the parent hover state changes, handle the overlay.
     */
    parentHover(currentState, previousState) {

      /**
       * There has been no parent hover for half a second.
       */
      if (!previousState && currentState) {
        this.overlayActive = true
        return
      }

      /**
       * Hold before aligning the overlay with the parent hover.
       * - This keeps the overlay open for 100ms to allow other hover events.
       */
      setTimeout(() => {
        this.overlayActive = this.parentHover
      }, timings.quick)
    },

    searchActive() {
      if (this.searchActive) {
        this.overlayActive = false
      }
    },

    overlayActive() {
      if (this.overlayActive) {
        this.searchActive = false
      }
    },
  },

  mounted() {
    nextTick(() => {
      this.setHeights(true)
    })

    window.visualViewport.addEventListener(
      'resize',
      debounce(() => this.handleResize(true), this.settings.throttle),
    )
  },

  methods: {
    getColumnCount,

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Determine if the message bar should be show or not.
     */
    showMessageBar() {
      if (!this.liquid.settings.message_bar_enable) {
        return false
      }

      if (
        this.liquid.pageType === 'product' &&
        this.liquid.settings.disable_message_bar_on_pdp
      ) {
        return false
      }

      return true
    },

    /**
     * Determine if the promo banner should be show or not.
     */
    showPromoBanner() {
      if (!this.liquid.settings.promo_banner_enable) {
        return false
      }

      if (
        this.liquid.pageType === 'product' &&
        this.liquid.settings.disable_promo_banner_on_pdp
      ) {
        return false
      }

      return true
    },

    /**
     * Handles the window resize event.
     */
    handleResize() {
      this.setHeights(true)

      /**
       * Resets the mega-nav overflow flag state.
       */
      this.megaNavOverflow = {
        wide: [],
        right: [],
      }
    },

    /**
     * Returns the dynamic classes for the navigation item.
     * @param {Object} item - The menu item.
     * @param {Number} index - The item position.
     * @returns {Object}
     */
    getNavigationItemClasses(item, index) {
      const key = this.getParentKey(item, index)
      const wide = this.megaNavOverflow.wide.includes(key)
      const right = this.megaNavOverflow.right.includes(key)

      return {
        'site-header__navigation-item--parent': this.itemHasChildren(item),
        'site-header__navigation-item--parent-hover': this.parentHover === key,
        'site-header__navigation-item--mega-nav-wide': wide,
        'site-header__navigation-item--mega-nav-right': right,
      }
    },

    /**
     * Sets the overflow state for the given mega-nav element.
     * - If the mega-nav overflows left or right then this updates the state.
     * - Allows for styles to be set based on the overflow position.
     *
     * @param {HTMLElement} element - The mega-nav element to check.
     */
    setMegaNavOverflowState(element) {
      const { right } = element.getBoundingClientRect()
      const key = element.parentNode.parentNode.id

      if (element.clientWidth > window.innerWidth) {
        if (!this.megaNavOverflow.wide.includes(key)) {
          this.megaNavOverflow.wide.push(key)
        }

        return
      }

      if (right > window.innerWidth) {
        if (!this.megaNavOverflow.right.includes(key)) {
          this.megaNavOverflow.right.push(key)
        }
      }
    },

    /**
     * Set heights of header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * - Height.promo is used for promo banners etc.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (this.inspectorMode) {
        return
      }

      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      this.height.visible = (this.headerState === 'closed')
        ? 0
        : this.$refs.siteHeader.offsetHeight

      this.height.promo = 0

      const siteMessagingRef = this.$refs.siteMessaging
      const sitePromoRef = this.$refs.sitePromo

      if (siteMessagingRef) {
        this.height.promo += siteMessagingRef.offsetHeight
      }

      if (sitePromoRef) {
        this.height.promo += sitePromoRef.offsetHeight
      }

      this.height.searchOverlayTop = this.$refs.masthead.offsetHeight + this.$refs.masthead.offsetTop - 1
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (this.isMobile || this.$variable('page.type') === 'cart') {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify inspector activate event.
     * - Disable fixed positioning.
     */
    shopifyInspectorActivate() {
      this.inspectorMode = true
      this.height.total = 0
      this.height.visible = 0
    },

    /**
     * Shopify inspector deactivate event.
     */
    shopifyInspectorDeactivate() {
      this.inspectorMode = false
      this.setHeights(true)
    },

    /**
     * Handles the parent mouse enter event.
     * @param {Object} event - The event object.
     * @param {String} key - The parent key.
     */
    handleParentMouseEvent(event, key) {
      if (event.type === 'mouseenter') {
        this.parentHover = key
        return
      }

      this.parentHover = false
    },

    /**
     * Toggles the parent hover based on a key.
     * @param {String} key - The parent key.
     */
    toggleParentHover(key) {
      if (this.parentHover === key) {
        this.parentHover = false
        return
      }

      this.parentHover = key
    },

    /**
     * Returns if the given item has children items.
     * @param {Object} item - The item to test.
     * @returns {Boolean}
     */
    itemHasChildren(item) {
      return item.items && item.items.length
    },

    /**
     * Returns the key for a parent item.
     * @param {Object} item - The menu item object.
     * @param {Number} index - The item index.
     * @returns {String}
     */
    getParentKey(item, index) {
      return `tier1-${index}-${item.handle}`
    },

    /**
     * Returns the events for the parent menu item.
     * @param {Object} item - The menu item object.
     * @param {Number} index - The item index.
     * @returns {Object}
     */
    getParentEvents(item, index) {
      const mouseHandler = (event) =>
        this.handleParentMouseEvent(
          event,
          this.getParentKey(item, index),
        )

      if (!this.itemHasChildren(item)) {
        return {}
      }

      return {
        mouseenter: mouseHandler,
        mouseleave: mouseHandler,
      }
    },

    /**
     * Send the mega nav link click event info to the data layer.
     * @param {String} label - clicked link title.
     * @param {Number} tier - clicked link tier.
     */
    megaNavLinkDataLayer(label, tier) {
      cnvs.EventBus.emit('Datalayer:megaNavLink', ({ label, tier }))
    },

    /**
     * Send the site header link click event info to the data layer.
     * @param {String} label - clicked link title.
     */
    siteHeaderLinkDataLayer(label) {
      cnvs.EventBus.emit('Datalayer:siteHeaderLink', label.toLowerCase())
    },

    /**
     * Send the site header logo click event info to the data layer.
     */
    siteHeaderLogoDataLayer() {
      cnvs.EventBus.emit('Datalayer:siteHeaderLogo')
    },
  },
}
</script>
